import * as React from 'react'
import { Link } from 'react-router-dom'
import { useIntl } from 'react-intl'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import { CircularProgress, TableHead } from '@mui/material'
import ArrowRightIcon from '../../../../assets/icons/Arrows-chevron/Arrow-Right.svg'
import ConfirmDelete from '../../../../components/dialogs/ConfirmDelete'
import { capitalize } from '../../../../utils/functions'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import CustomInput from '../../../../components/muiComponents/customInput'
import { GroupState, TeacherState } from '../../../../features/types'

const MENTOR_ROLES = ['CURATOR', 'HEAD_CURATOR']
import EditGroupDialog from './EditGroupDialog'
import { DEFAULT_PAGE_SIZE } from '../../../../utils/constants'
import { ui } from '../../../../utils/uiTokens'

interface Props {
    groups: GroupState[]
    curators: TeacherState[]
    isFetchingCurators: boolean
    onDelete?: (subject: number) => void
}

const GroupsTable: React.FC<Props> = ({
    groups,
    curators,
    isFetchingCurators,
    onDelete,
}) => {
    const { formatMessage, locale } = useIntl()
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGE_SIZE)
    const [query, setQuery] = React.useState('')

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    // В группе мог остаться тот, кто уже не ментор (роль сменили или удалили):
    // подписываем его, а не выводим пустым местом
    const getCuratorsOfGroup = (group) => {
        const result = group?.curatorIds?.map?.((curatorId) => {
            const curatorInfo = curators?.find(
                (curator) => curator?.id === curatorId,
            )
            if (!curatorInfo) {
                return formatMessage({ id: 'curatorDeleted' })
            }
            const name = curatorInfo?.firstName + ' ' + curatorInfo?.lastName
            return MENTOR_ROLES.includes(curatorInfo?.role)
                ? name
                : `${name} (${formatMessage({ id: 'curatorNotMentor' })})`
        })

        return curators?.length ? result?.join(', ') : ''
    }

    // Фильтруем на клиенте: /student-group отдаёт весь список без пагинации,
    // так что данные и так уже все в браузере.
    // Ищем и по названию группы, и по менторам - обе колонки видны в таблице.
    const search = query.trim().toLowerCase()
    const filtered = !search
        ? groups ?? []
        : (groups ?? []).filter(
              (group) =>
                  group?.title?.toLowerCase().includes(search) ||
                  getCuratorsOfGroup(group)?.toLowerCase().includes(search),
          )

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filtered.length) : 0

    return (
        <Box>
            <CustomInput
                value={query}
                margin="none"
                type="text"
                size="small"
                placeholder={formatMessage({ id: 'search' })}
                sx={{ mb: 2, width: 320, maxWidth: '100%' }}
                onChange={(e) => {
                    setQuery(e.target.value)
                    setPage(0)
                }}
            />

            <TableContainer
                component={Paper}
                sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell>
                                {formatMessage({ id: 'group' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'curators' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'subjects' })}
                            </TableCell>

                            <TableCell align="center">
                                {formatMessage({ id: 'studentCount' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'details' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'edit' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'delete' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {(rowsPerPage > 0
                            ? filtered.slice(
                                  page * rowsPerPage,
                                  page * rowsPerPage + rowsPerPage,
                              )
                            : filtered
                        ).map((row) => (
                            <TableRow key={row?.id}>
                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {row?.title}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {isFetchingCurators ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        getCuratorsOfGroup(row)
                                    )}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {row?.[`subjects${capitalize(locale)}`]}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                    align="center"
                                >
                                    {row?.studentCount}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                    style={{ width: 60 }}
                                    align="right"
                                >
                                    <Link
                                        to={
                                            '/groups/' +
                                            row?.id +
                                            '?course=' +
                                            row?.courseId +
                                            '&groupName=' +
                                            row?.title
                                        }
                                    >
                                        <IconButton
                                            sx={{ background: ui.track }}
                                        >
                                            <img
                                                src={ArrowRightIcon}
                                                alt="edit"
                                            />
                                        </IconButton>
                                    </Link>
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                    align="right"
                                    style={{ width: 60 }}
                                >
                                    <EditGroupDialog editGroup={row} />
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                    style={{ width: 60 }}
                                    align="right"
                                >
                                    <ConfirmDelete
                                        confirmAction={() =>
                                            onDelete(row?.id as number)
                                        }
                                    />
                                </TableCell>
                            </TableRow>
                        ))}

                        {emptyRows > 0 && (
                            <TableRow style={{ height: 53 * emptyRows }}>
                                <TableCell colSpan={6} />
                            </TableRow>
                        )}

                        {!filtered.length && (
                            <TableRow style={{ height: 53 * emptyRows }}>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    align="center"
                                    colSpan={7}
                                >
                                    {formatMessage({ id: 'noData' })}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>

                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[
                                    5,
                                    10,
                                    25,
                                    { label: 'All', value: -1 },
                                ]}
                                colSpan={7}
                                count={filtered.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                SelectProps={{
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    native: true,
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                                sx={{ border: 0 }}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    )
}

export default GroupsTable
